import { TeacherOrigin, TeacherStatus } from 'constants/teacherRecruitment';
import { EApiStatus } from 'constants/common';
import { PATH } from 'routes/routeConfig';
import { Icon, SvgName } from 'components/Icon';
import { t } from 'utils/i18n';
import { useEffect, useState } from 'react';
import axios from 'plugins/api/axios';
import { useProfileService } from 'services/ProfileService';
import { useLocation } from 'react-router-dom';
import { TeacherRecruitmentDto } from 'types/teacherRecruitment';
import { useFeatureToggle, useSnackbar } from 'hooks/index';
import {
  getOfficialApplicationReviews,
  getTeacherOrigin,
} from 'utils/teacherRecruitment';
import { ERemoteConfigParameter } from 'services/FeatureToggleService/remoteConfigService.constants';
import { getChange } from 'pages/TeacherApplication/TeacherApplication.utils';
import { useLatestContractStatus } from 'hooks/useLatestContractStatus';

const { teacherRecruit } = axios;
// ----------------------------------------------------------------------

const getIcon = (name: SvgName) => <Icon name={name} />;

const ICONS = {
  calendar: getIcon(SvgName.Calendar),
  idCard: getIcon(SvgName.IdCard),
  myCourse: getIcon(SvgName.BookReader),
  suitcase: getIcon(SvgName.Suitcase),
  oneAnswer: getIcon(SvgName.OneAnswer),
  requestSignEmploymentContract: getIcon(SvgName.Receipt),
  error: getIcon(SvgName.Error),
};

export interface NavItem {
  title: string;
  path: string;
  icon?: JSX.Element;
  children?: NavItem[];
}

export enum CrossCourseApplicationStage {
  EDITING = 1,
  SUBMITTED = 2,
}

export default () => {
  const { profile, updateInfoTeacherRecruitment, infoTeacherRecruitment } =
    useProfileService();
  const username = profile.oneClubId;
  const { pathname } = useLocation();

  const { enqueueSnackbar } = useSnackbar();
  const [enabledFeatures] = useFeatureToggle();
  const isOpenOneAnswer = enabledFeatures(ERemoteConfigParameter.ONE_ANSWER);

  const [isTurnOnCrossSubject, setIsTurnOnCrossSubject] = useState(false);
  const [isTurnOnTerminatedContract, setIsTurnOnTerminatedContract] =
    useState(false);
  const [
    isTurnOnRequestSignEmploymentContract,
    setIsTurnOnRequestSignEmploymentContract,
  ] = useState(false);

  const [teacherRecruitData, setTeacherRecruitData] =
    useState<TeacherRecruitmentDto | null>(null);

  const [teacherOriginType, setTeacherOriginType] = useState<
    TeacherOrigin | undefined
  >(undefined);

  const [curNavs, setCurNavs] = useState<
    {
      subheader: string;
      items: NavItem[];
    }[]
  >([]);
  const { isSigned } = useLatestContractStatus();

  const updateTeacherOrigin = async () => {
    const type = await getTeacherOrigin();
    setTeacherOriginType(type);
  };

  useEffect(() => {
    if (!username) {
      return;
    }
    updateTeacherOrigin();
    teacherRecruit.get().then(res => {
      if (res.status === EApiStatus.SUCCESS && res.data) {
        const data = res.data;
        setTeacherRecruitData(data);
        setIsTurnOnCrossSubject(!!data.isTurnOnCrossSubjectUI);
        setIsTurnOnTerminatedContract(!!data.sentTerminatedContractTime);
        setIsTurnOnRequestSignEmploymentContract(
          !!data.requestSignContractFromMMSTime && !data.contractSignedTime,
        );
        const diff = getChange(infoTeacherRecruitment, {
          ...infoTeacherRecruitment,
          ...data,
        });

        if (updateInfoTeacherRecruitment && diff) {
          (async () => {
            await updateInfoTeacherRecruitment(data as TeacherRecruitmentDto);
          })();
        }
      } else {
        if (profile.status === TeacherStatus.UNVERIFIED) {
          enqueueSnackbar(
            t(
              'layouts.dashboard.navbar.unverifiedNavConfig.getUserFailed',
              'Get user failed',
            ),
            { variant: 'error' },
          );
        }
      }
    });
  }, [username, pathname]);

  const getCurrentApplicationState = (
    teacherRecruitData: TeacherRecruitmentDto,
    teacherOriginType: TeacherOrigin,
  ) => {
    const crossCourseApplications = getOfficialApplicationReviews(
      teacherRecruitData,
      teacherOriginType,
    );
    if (
      crossCourseApplications &&
      crossCourseApplications.length > 0 &&
      crossCourseApplications[crossCourseApplications.length - 1].applyTime
    ) {
      // show application status
      return CrossCourseApplicationStage.SUBMITTED;
    }
    return CrossCourseApplicationStage.EDITING;
  };

  useEffect(() => {
    const navs: NavItem[] = [
      {
        title: t('header.schedule', '我的行程'),
        path: PATH.SCHEDULE,
        icon: ICONS.calendar,
      },
      {
        title: t('header.course', '我的課程'),
        path: PATH.COURSE,
        icon: ICONS.myCourse,
        children: [
          {
            title: t('header.courseList', '課程列表'),
            path: PATH.COURSE_LIST,
          },
          {
            title: t('header.courseAsk', '排課請求'),
            path: PATH.COURSE_ASK,
          },
        ],
      },
    ];

    if (isOpenOneAnswer) {
      navs.push({
        title: 'OneAnswer',
        path: PATH.ONE_ANSWER,
        icon: ICONS.oneAnswer,
      });
    }

    const accountNav = {
      title: '會員中心',
      path: PATH.ACCOUNT,
      icon: ICONS.idCard,
      children: [
        {
          title: t('header.profile', '個人資料'),
          path: PATH.ACCOUNT_PROFILE,
        },
        {
          title: '無法到課紀錄',
          path: PATH.ACCOUNT_LEAVE_RECORD,
        },
        {
          title: '授課報酬確認',
          path: PATH.ACCOUNT_COMPENSATION_CONFIRMATION,
        },
      ],
    };
    if (!isSigned) {
      accountNav.children.push({
        title: '契約更新',
        path: PATH.ACCOUNT_CONTRACT_UPDATE,
      });
    }
    navs.push(accountNav);

    if (isTurnOnCrossSubject) {
      const applicationNav = {
        title: t('header.teacherApplication', '合作申請'),
        path: PATH.CROSS_SUBJECT,
        icon: ICONS.suitcase,
        children: [
          {
            title: t('header.trialInstructions', '試教說明'),
            path: PATH.CROSS_SUBJECT_TRIAL_INFORMATION,
          },
        ],
      };

      if (teacherRecruitData && teacherOriginType) {
        const currentApplicationState = getCurrentApplicationState(
          teacherRecruitData,
          teacherOriginType,
        );

        if (currentApplicationState >= CrossCourseApplicationStage.SUBMITTED) {
          applicationNav.children.push({
            title: t('header.teacherApplicationStatus', '試教說明'),
            path: PATH.CROSS_SUBJECT_APPLICATION_STATUS,
          });
        }
      }
      navs.push(applicationNav);
    }

    if (isTurnOnRequestSignEmploymentContract) {
      const applicationNav = {
        title: `${t('header.requestSignEmploymentContract', '教師聘用合約')}`,
        path: PATH.REQUEST_SIGN_EMPLOYMENT_CONTRACT,
        icon: ICONS.requestSignEmploymentContract,
      };

      navs.push(applicationNav);
    }

    if (isTurnOnTerminatedContract) {
      const applicationNav = {
        title: t('header.teacherTerminatedContract', '授課終止合約'),
        path: PATH.TEACHER_TERMINATED_CONTRACT,
        icon: ICONS.suitcase,
      };

      navs.push(applicationNav);
    }

    setCurNavs([
      {
        subheader: `general v${import.meta.env.VITE_APP_VERSION || '1.0.0'}`, //  general v3.3.0
        items: navs,
      },
    ]);
  }, [
    teacherRecruitData,
    teacherOriginType,
    isTurnOnCrossSubject,
    isOpenOneAnswer,
  ]);

  return curNavs;
};
